/** SUNTRONIX ONE — Smart Attach panel (Order 360 + Product 360).
 *  Shows ONE Smart Attach cross-sell recommendations and, for orders, the
 *  outreach status (emails sent / customer interest / conversions).
 */
import { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, Send, Package, ShieldCheck, Wrench, Plug, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/app/vibe/StatusPill";

const fmtMoney = (n, cur = "INR") => {
  if (n == null) return "Price on request";
  const sym = cur === "INR" ? "\u20B9" : cur + " ";
  return `${sym}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const GROUP_ICON = {
  PROTECTION: ShieldCheck, SERVICES: Wrench, CONNECTIVITY: Plug, POWER: Plug,
};

function RecCard({ r }) {
  const Icon = GROUP_ICON[r.group] || Package;
  const isService = r.group === "SERVICES";
  return (
    <div data-testid={`sa-rec-${(r.sku || r.name || "").toString().toLowerCase()}`}
      className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors">
      <div className={"h-9 w-9 rounded-lg grid place-items-center shrink-0 " +
        (isService ? "bg-indigo-50 text-indigo-600" : "bg-slate-50 text-slate-500")}>
        <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium text-slate-900">{r.name}</span>
          {isService && <span className="text-[9px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Service</span>}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">{r.compatibility_label}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] font-mono text-slate-600">{fmtMoney(r.price, r.currency)}</span>
          {r.group && <span className="text-[10px] uppercase tracking-wide text-slate-400">· {r.group}</span>}
        </div>
      </div>
    </div>
  );
}

export default function SmartAttachPanel({ mode, orderId, productName, brand }) {
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState(null);
  const [opps, setOpps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "product" && productName) {
        const { data } = await api.get("/smart-attach/for-product", { params: { name: productName, brand } });
        setRecs(data);
      } else if (mode === "order" && orderId) {
        const { data } = await api.get("/smart-attach/opportunities", { params: { order_id: orderId } });
        setOpps(data.items || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [mode, orderId, productName, brand]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/smart-attach/generate-order/${orderId}`);
      if (data.created) toast.success("Smart Attach recommendations generated");
      else toast.info("No new recommendations for this order");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate"); }
    finally { setBusy(false); }
  };

  const cards = mode === "product"
    ? [...(recs?.primary || []), ...(recs?.extra || [])]
    : [];

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-4" data-testid="smart-attach-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-700">
          <Sparkles className="h-4 w-4" strokeWidth={2} /> ONE Smart Attach
        </div>
        {mode === "order" && (
          <button onClick={generate} disabled={busy} data-testid="sa-generate-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-medium disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Generate
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading recommendations…
        </div>
      ) : mode === "product" ? (
        cards.length ? (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {cards.map((r, i) => <RecCard key={r.id || i} r={r} />)}
          </div>
        ) : (
          <div className="text-[12px] text-slate-500 py-5 text-center" data-testid="sa-empty">
            No cross-sell recommendations mapped for this product yet.
          </div>
        )
      ) : (
        opps.length ? (
          <div className="mt-3 space-y-3">
            {opps.map((o) => (
              <div key={o.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`sa-opp-${o.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-medium text-slate-800">{o.code} · {o.product_name}</div>
                  <StatusPill status={o.status} label={String(o.status || "").replace(/_/g, " ")} />
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-[11px] text-slate-500">
                  <span><b className="text-slate-700">{o.recommendation_count || 0}</b> recommendations</span>
                  <span><b className="text-slate-700">{(o.interests || []).length}</b> interested</span>
                  <span><b className="text-slate-700">{(o.converted_items || []).length}</b> converted</span>
                </div>
                {(o.snapshot?.primary || []).length > 0 && (
                  <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(o.snapshot.primary || []).slice(0, 4).map((r, i) => <RecCard key={i} r={r} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[12px] text-slate-500 py-5 text-center flex flex-col items-center gap-2" data-testid="sa-empty">
            <span>No Smart Attach outreach yet for this order.</span>
            <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
              Tap Generate to build recommendations <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        )
      )}
    </div>
  );
}
